import { useEffect, useState } from "react";
import { DEFAULT_PLANET_SET, PLANET_SETS, type AdminEvent, type PlanetSet } from "@shared";
import * as api from "./adminApi";

interface Props {
  selectedId: string | null;
  onSelect: (event: AdminEvent | null) => void;
}

const emptyDraft = {
  name: "",
  startsAt: "",
  endsAt: "",
  isActive: true,
  explorerModeEnabled: false,
  planetSet: DEFAULT_PLANET_SET as PlanetSet,
  contestEnabled: false,
  seedKitObjects: true,
};

/**
 * Les deux séries d'astres de la mission 2. Le nombre de leurres est annoncé :
 * c'est lui qui change la difficulté de fin de partie, et rien d'autre dans
 * l'interface ne le dirait.
 */
const PLANET_SET_LABELS: Record<PlanetSet, { titre: string; detail: string }> = {
  officielles: {
    titre: "Officielles",
    detail: "7 astres · un seul leurre (la Terre) — la dernière dragouille se joue à pile ou face",
  },
  ai: {
    titre: "Dessinées",
    detail: "9 astres · trois leurres (Soleil, Vénus, Terre) — aucune réponse par élimination",
  },
};

/** Un `datetime-local` ne comprend pas l'ISO complet : on tronque aux minutes. */
function toLocalInput(iso: string | null): string {
  return iso === null ? "" : iso.slice(0, 16);
}

export default function EventsPanel({ selectedId, onSelect }: Props) {
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    try {
      setEvents(await api.listEvents());
    } catch {
      setError("Impossible de charger les événements.");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    const input = {
      name: draft.name,
      startsAt: draft.startsAt === "" ? null : new Date(draft.startsAt).toISOString(),
      endsAt: draft.endsAt === "" ? null : new Date(draft.endsAt).toISOString(),
      isActive: draft.isActive,
      explorerModeEnabled: draft.explorerModeEnabled,
      planetSet: draft.planetSet,
      contestEnabled: draft.contestEnabled,
      seedKitObjects: draft.seedKitObjects,
    };

    try {
      if (editingId === null) await api.createEvent(input);
      else await api.updateEvent(editingId, input);
      setDraft(emptyDraft);
      setEditingId(null);
      await refresh();
    } catch {
      setError("Enregistrement impossible. Vérifie le nom et les dates.");
    }
  }

  async function remove(event: AdminEvent): Promise<void> {
    const confirmed = window.confirm(
      `Supprimer l'événement « ${event.name} » ?\n\n` +
        "Cela supprime AUSSI ses séries, ses objets, ses codes et tous les " +
        "résultats des joueurs sur cet événement. Action irréversible.",
    );
    if (!confirmed) return;

    await api.deleteEvent(event.id);
    if (selectedId === event.id) onSelect(null);
    await refresh();
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Événements</h2>

      {error !== null && <p className="form__error">{error}</p>}

      <ul className="list">
        {events.map((event) => (
          <li
            key={event.id}
            className={`list__item ${selectedId === event.id ? "list__item--active" : ""}`}
          >
            <button
              type="button"
              className="list__main"
              onClick={() => {
                onSelect(event);
              }}
            >
              <span className="list__name">{event.name}</span>
              <span className="list__meta">
                {event.isActive ? "★ active" : "archivée"}
                {event.explorerModeEnabled && " · mode Explorateur"}
                {` · astres ${PLANET_SET_LABELS[event.planetSet].titre.toLowerCase()}`}
                {event.contestEnabled && " · concours"}
                {event.startsAt !== null && ` · dès le ${event.startsAt.slice(0, 10)}`}
                {event.endsAt !== null && ` · jusqu'au ${event.endsAt.slice(0, 10)}`}
              </span>
            </button>

            <div className="list__actions">
              <button
                type="button"
                className="button button--small"
                onClick={() => {
                  setEditingId(event.id);
                  setDraft({
                    name: event.name,
                    startsAt: toLocalInput(event.startsAt),
                    endsAt: toLocalInput(event.endsAt),
                    isActive: event.isActive,
                    explorerModeEnabled: event.explorerModeEnabled,
                    planetSet: event.planetSet,
                    contestEnabled: event.contestEnabled,
                    seedKitObjects: false,
                  });
                }}
              >
                Modifier
              </button>
              <button
                type="button"
                className="button button--small button--danger"
                onClick={() => void remove(event)}
              >
                Supprimer
              </button>
            </div>
          </li>
        ))}
        {events.length === 0 && <li className="list__empty">Aucun événement pour l'instant.</li>}
      </ul>

      <form className="form" onSubmit={(e) => void submit(e)}>
        <h3 className="form__title">
          {editingId === null ? "Nouvel événement" : "Modifier l'événement"}
        </h3>

        <label className="form__label" htmlFor="event-name">
          Nom
        </label>
        <input
          id="event-name"
          className="form__input"
          type="text"
          value={draft.name}
          placeholder="Anniv Léo, Kermesse 2026…"
          onChange={(e) => {
            setDraft({ ...draft, name: e.target.value });
          }}
        />

        <div className="form__row">
          <div className="form__col">
            <label className="form__label" htmlFor="event-start">
              Début (optionnel)
            </label>
            <input
              id="event-start"
              className="form__input"
              type="datetime-local"
              value={draft.startsAt}
              onChange={(e) => {
                setDraft({ ...draft, startsAt: e.target.value });
              }}
            />
          </div>
          <div className="form__col">
            <label className="form__label" htmlFor="event-end">
              Fin (optionnel)
            </label>
            <input
              id="event-end"
              className="form__input"
              type="datetime-local"
              value={draft.endsAt}
              onChange={(e) => {
                setDraft({ ...draft, endsAt: e.target.value });
              }}
            />
          </div>
        </div>

        <label className="form__check">
          <input
            type="checkbox"
            checked={draft.isActive}
            onChange={(e) => {
              setDraft({ ...draft, isActive: e.target.checked });
            }}
          />
          Mission active — activer celle-ci archive automatiquement les autres
        </label>

        <label className="form__check">
          <input
            type="checkbox"
            checked={draft.explorerModeEnabled}
            onChange={(e) => {
              setDraft({ ...draft, explorerModeEnabled: e.target.checked });
            }}
          />
          Proposer le mode Explorateur (scan et code) — à venir
        </label>

        <fieldset className="form__fieldset">
          <legend className="form__legend">Astres de la mission 2</legend>
          {PLANET_SETS.map((set) => (
            <label className="form__check" key={set}>
              <input
                type="radio"
                name="planet-set"
                value={set}
                checked={draft.planetSet === set}
                onChange={() => {
                  setDraft({ ...draft, planetSet: set });
                }}
              />
              <span>
                {PLANET_SET_LABELS[set].titre}
                <span className="form__check-detail"> — {PLANET_SET_LABELS[set].detail}</span>
              </span>
            </label>
          ))}

          {/* Un changement de série reconstruit les astres : le dire AVANT
              d'enregistrer, pas après. */}
          {editingId !== null &&
            draft.planetSet !== events.find((e) => e.id === editingId)?.planetSet && (
              <p className="form__warning">
                Changer de série remplace les astres de cette mission. Les parties déjà jouées
                gardent leur chrono, mais le détail de leurs associations ne sera plus lisible — et
                un jeune en train de jouer devra refaire les siennes. À éviter pendant l'événement.
              </p>
            )}
        </fieldset>

        {/* Le concours collecte des adresses courriel : c'est la SEULE donnée
            personnelle de l'application, et elle concerne des mineurs. Le dire
            ici, à l'endroit où on l'active, plutôt que de compter sur le fait
            que l'admin se souvienne de la page de consentement. */}
        <fieldset className="form__fieldset">
          <legend className="form__legend">Concours de fin</legend>
          <label className="form__check">
            <input
              type="checkbox"
              checked={draft.contestEnabled}
              onChange={(e) => {
                setDraft({ ...draft, contestEnabled: e.target.checked });
              }}
            />
            <span>
              Proposer le concours
              <span className="form__check-detail">
                {" "}
                — s'ouvre au jeune une fois les DEUX missions terminées
              </span>
            </span>
          </label>

          {draft.contestEnabled && (
            <p className="form__warning">
              Le concours collecte une adresse courriel par participation — la seule donnée
              personnelle de l'application, et les joueurs sont mineurs. Le texte affiché se règle
              dans l'onglet « Textes » ; le consentement du concours, lui, vit dans{" "}
              <code>content/consent.fr.md</code> et <code>content/consent.en.md</code>, et se
              modifient dans l'éditeur, pas ici.
            </p>
          )}
        </fieldset>

        {/* Seulement à la création : modifier une mission ne doit pas y
            réinjecter dix objets en double. */}
        {editingId === null && (
          <label className="form__check">
            <input
              type="checkbox"
              checked={draft.seedKitObjects}
              onChange={(e) => {
                setDraft({ ...draft, seedKitObjects: e.target.checked });
              }}
            />
            Ajouter d'emblée les objets du kit — prêt à jouer, à élaguer ensuite
          </label>
        )}

        <div className="button-row">
          <button type="submit" className="button button--primary" disabled={draft.name === ""}>
            {editingId === null ? "Créer" : "Enregistrer"}
          </button>
          {editingId !== null && (
            <button
              type="button"
              className="button button--ghost"
              onClick={() => {
                setEditingId(null);
                setDraft(emptyDraft);
              }}
            >
              Annuler
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
